/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { inject, Injectable } from '@angular/core';
import { AlfrescoApiService } from '@alfresco/adf-content-services';
import { NodesApi } from '@alfresco/js-api';
import { defer, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';

/**
 * The application's shared settings documents in `Data Dictionary/ACA` —
 * branding, menu policies, watermark, workflow switch and catalogue, document
 * type labels.
 *
 * Each of those used to ask for its document by path, and every document an
 * administrator had not saved yet answered 404: four or five red errors in the
 * console on every start, and as many round trips. The folder is listed once
 * instead, and a document that is not in it costs nothing to look for.
 */
@Injectable({ providedIn: 'root' })
export class AcaConfigFolderService {
  static readonly PATH = 'Data Dictionary/ACA';

  private readonly api = inject(AlfrescoApiService);
  private listing$?: Observable<Map<string, string>>;

  /** The node id of a document in the folder; errors when it is not there. */
  find(fileName: string): Observable<string> {
    return this.listing().pipe(
      switchMap((ids) => (ids.has(fileName) ? of(ids.get(fileName) as string) : throwError(() => new Error(`${fileName} not saved yet`))))
    );
  }

  /** Called after a document is created, so the next lookup lists the folder again. */
  forget(): void {
    this.listing$ = undefined;
  }

  private listing(): Observable<Map<string, string>> {
    if (!this.listing$) {
      this.listing$ = defer(() =>
        from(
          new NodesApi(this.api.getInstance() as any).listNodeChildren('-root-', {
            relativePath: AcaConfigFolderService.PATH,
            maxItems: 200,
            fields: ['id', 'name']
          } as any) as Promise<any>
        )
      ).pipe(
        map((page: any) => new Map<string, string>((page?.list?.entries ?? []).map((e: any) => [e.entry.name, e.entry.id] as [string, string]))),
        catchError(() => {
          // no folder yet, or no session: nothing is saved — but ask again next time
          this.listing$ = undefined;
          return of(new Map<string, string>());
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.listing$;
  }
}
